import React, { useState, useEffect, useRef } from 'react';
import { searchMovies } from '../services/tmdbService';
import { Movie, SupportedLang } from '../types';
import { TRANSLATIONS } from '../constants';

interface MovieSearchProps {
  onGuess: (movie: Movie) => void;
  onSkip?: () => void;
  showSkip?: boolean;
  lang: SupportedLang;
  disabled: boolean;
}

const MovieSearch: React.FC<MovieSearchProps> = ({ onGuess, onSkip, showSkip, lang, disabled }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Movie[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.trim()) {
        const data = await searchMovies(query, lang);
        setResults(data);
        setIsOpen(true);
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, lang]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (movie: Movie) => {
    onGuess(movie);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const handleSkipClick = () => {
    if (onSkip) {
      onSkip();
      setQuery('');
      setResults([]);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative w-full" ref={searchRef}>
      <div className="flex flex-col sm:flex-row gap-3 lg:gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={TRANSLATIONS.guessPlaceholder[lang]}
          disabled={disabled}
          className="flex-1 bg-zinc-100 dark:bg-surface border border-zinc-300 dark:border-zinc-800 rounded-xl px-4 sm:px-5 py-3.5 lg:py-2 text-[16px] lg:text-[13px] focus:outline-none focus:border-prestige-gold dark:focus:border-prestige-gold transition-all disabled:opacity-50 text-ink dark:text-off-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 font-bold min-h-[48px] lg:min-h-[40px]"
        />
        <div className="flex gap-2 sm:gap-3 lg:gap-2">
          <button
            onClick={() => {
              if (query && results.length > 0) {
                handleSelect(results[0]);
              } else if (query) {
                onGuess({ id: -1, title: query, backdrop_path: '', poster_path: '', release_date: '' });
              }
            }}
            disabled={disabled || !query}
            className="flex-1 sm:flex-none bg-prestige-gold hover:bg-amber-500 text-black font-bold uppercase tracking-[0.15em] px-6 sm:px-8 py-3.5 lg:py-2 rounded-[12px] transition-all disabled:opacity-30 text-base lg:text-sm shadow-lg shadow-prestige-gold/20 min-h-[48px] lg:min-h-[40px] whitespace-nowrap"
          >
            {TRANSLATIONS.submit[lang]}
          </button>
          
          {showSkip && (
            <button
              onClick={handleSkipClick}
              disabled={disabled}
              className="flex-1 sm:flex-none bg-transparent border-2 border-zinc-300 dark:border-zinc-700 hover:border-prestige-gold text-zinc-500 dark:text-zinc-400 hover:text-prestige-gold font-bold uppercase tracking-[0.15em] px-6 sm:px-8 py-3.5 lg:py-2 rounded-[12px] transition-all disabled:opacity-30 text-base lg:text-sm min-h-[48px] lg:min-h-[40px] whitespace-nowrap"
            >
              {TRANSLATIONS.skip[lang]}
            </button>
          )}
        </div>
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute bottom-full mb-3 sm:mb-4 w-full bg-off-white dark:bg-midnight border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden shadow-2xl z-50 max-h-48 sm:max-h-60 lg:max-h-44 overflow-y-auto animate-fade-in divide-y divide-zinc-200 dark:divide-zinc-900">
          {results.map((movie) => (
            <button
              key={movie.id}
              onClick={() => handleSelect(movie)}
              className="w-full text-left px-3 sm:px-4 py-3 sm:py-4 lg:py-2.5 hover:bg-zinc-100 dark:hover:bg-surface transition-colors flex items-center gap-4 group"
            >
              <div className="w-10 sm:w-12 lg:w-9 aspect-[2/3] shrink-0 bg-zinc-200 dark:bg-zinc-800 rounded overflow-hidden shadow-sm">
                {movie.poster_path ? (
                  <img 
                    src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[8px] font-black text-zinc-400 uppercase">NO REEL</div>
                )}
              </div>
              <div className="flex flex-col flex-1 overflow-hidden">
                <span className="font-extrabold text-[14px] sm:text-base lg:text-[12px] text-ink dark:text-off-white tracking-tight group-hover:text-prestige-gold transition-colors truncate">{movie.title}</span>
                <span className="text-[10px] sm:text-[11px] lg:text-[9px] uppercase font-black text-zinc-500 dark:text-zinc-600 mt-0.5 tracking-[0.1em]">{movie.release_date?.split('-')[0] || 'Unknown'}</span>
              </div>
              <div className="w-7 h-7 bg-zinc-200 dark:bg-surface rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shrink-0">
                 <svg className="w-4 h-4 text-prestige-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" /></svg>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MovieSearch;